// src/components/chat/tools/DraftConceptContentUI.jsx
//
// Renders the `draft_concept_content` tool call as an inline card in the
// chat panel. While the sub-agent is running, shows a "drafting..." state.
// On completion, parses the tool's JSON result and renders the draft
// preview with Save / Refine / Discard actions.
//
// Reference pattern: assistant-ui/assistant-ui
// `apps/docs/content/docs/guides/multi-agent.mdx` — `makeAssistantToolUI`
// is the path-(c) renderer for sub-agent tools that return structured
// results (NOT live nested message threads — that's a future phase if we
// ever want to surface the writer→critic conversation).
import React, { useState } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { Check, RefreshCw, Trash2, Loader2 } from 'lucide-react';

import { apiService, handleApiError } from '../../../services/api';
import { useConfirm } from '../../ui/ConfirmDialog';

function safeJsonParse(s) {
  if (typeof s !== 'string') return null;
  try { return JSON.parse(s); } catch { return null; }
}

function DraftCard({ args, result }) {
  const [committing, setCommitting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [discarded, setDiscarded] = useState(false);
  const [error, setError] = useState(null);

  const isError = result?.status !== 'DRAFT_READY' || !result?.draft_id;

  const handleSave = async () => {
    if (!result?.draft_id) return;
    setError(null);
    setCommitting(true);
    try {
      const res = await apiService.commitDraft(result.draft_id);
      setSavedId(res.data?.content_id || result.draft_id);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  const confirm = useConfirm();
  const handleDiscard = async () => {
    if (!result?.draft_id) return;
    const ok = await confirm({
      title: 'Discard this draft?',
      body: 'It can’t be recovered.',
      confirmLabel: 'Discard',
      danger: true,
    });
    if (!ok) return;
    setError(null);
    setDiscarding(true);
    try {
      await apiService.discardDraft(result.draft_id);
      setDiscarded(true);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setDiscarding(false);
    }
  };

  if (isError) {
    return (
      <div className="my-2 rounded-lg border border-line bg-[var(--error-soft)] px-3 py-2">
        <p className="text-xs font-medium text-status-bad">Couldn't draft</p>
        <p className="text-xs text-mutedink mt-1">
          {typeof result === 'string' ? result : 'No draft was produced.'}
        </p>
      </div>
    );
  }

  return (
    <div className="tcard is-arriving my-2" role="group" aria-label="drafted note">
      <div className="tcard-head">
        <span className="tc-kicker">Drafted note</span>
        <span className="tc-pill">not saved</span>
        <span className="ml-auto font-mono text-[10px] text-faint">
          {result.content_full_chars
            ? `${result.content_full_chars.toLocaleString()} chars`
            : ''}
        </span>
      </div>
      <div className="tcard-body">
        <div className="dc-title">{args?.concept || result.concept}</div>
        {result.summary && (
          <p className="text-xs italic text-mutedink mb-2">{result.summary}</p>
        )}
        <pre className="dc-body max-h-48 overflow-y-auto text-xs whitespace-pre-wrap">
          {result.content_preview}
          {result.content_full_chars > (result.content_preview || '').length ? '…' : ''}
        </pre>
      </div>
      {renderDraftFooter({
        savedId, discarded, committing, discarding,
        result, args, handleSave, handleDiscard,
      })}
      {error && (
        <p className="border-t border-line px-3 py-2 text-xs text-status-bad">
          {error}
        </p>
      )}
    </div>
  );
}

// Refine = type a follow-up message asking the agent to refine. The agent
// will call draft_concept_content again with updated instruction. We
// pre-fill the composer instead of auto-sending — gives the user a chance
// to edit the refinement brief.
function renderDraftFooter({
  savedId, discarded, committing, discarding,
  result, args, handleSave, handleDiscard,
}) {
  if (savedId) {
    return (
      <div className="dc-resolved saved">
        ✓ saved — the notes for {result.concept} are live
      </div>
    );
  }
  if (discarded) {
    return <div className="dc-resolved">· discarded</div>;
  }
  return (
    <div className="dc-actions">
      <button
        type="button"
        className="fn-btn fn-btn--primary"
        disabled={committing || discarding}
        onClick={handleSave}
      >
        {committing
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <Check className="w-3.5 h-3.5" />}
        Save to notes
      </button>
      <RefineButton args={args} result={result} disabled={committing || discarding} />
      <button
        type="button"
        className="fn-btn fn-btn--ghost ml-auto"
        disabled={committing || discarding}
        onClick={handleDiscard}
      >
        {discarding
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <Trash2 className="w-3 h-3" />}
        Discard
      </button>
    </div>
  );
}

function RefineButton({ args, result, disabled }) {
  const handleClick = () => {
    const seed = `Refine the draft on ${args?.concept || result.concept}: `;
    // Best-effort: focus the composer textarea. assistant-ui's primitive
    // doesn't expose an imperative API; the user types their refinement
    // and sends. The pre-fill via DOM is intentionally light-touch.
    const ta = document.querySelector('textarea[placeholder="Type a message"]');
    if (ta) {
      const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
      setter.call(ta, seed);
      ta.dispatchEvent(new Event('input', { bubbles: true }));
      ta.focus();
    }
  };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={handleClick}
      className="fn-btn"
    >
      <RefreshCw className="w-3 h-3" />
      Refine
    </button>
  );
}

export const DraftConceptContentUI = makeAssistantToolUI({
  toolName: 'draft_concept_content',
  render: ({ args, result, status }) => {
    const parsed = typeof result === 'string' ? safeJsonParse(result) : result;
    if (status?.type === 'running' || (!parsed && !result)) {
      return (
        <div className="my-2 inline-flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-xs text-mutedink">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-accent" />
          Drafting {args?.concept || 'notes'}…
        </div>
      );
    }
    return <DraftCard args={args} result={parsed || result} />;
  },
});

